"use strict";

/**
 * SageReportHistoryController
 *
 * Unified history listing for both Invoice and Receipt SAGE exports. The user
 * picks a project + filters and clicks Search to load the page — there is no
 * auto-fetch on filter / tab / project change because the queries are
 * potentially heavy and the user is expected to fine-tune filters before
 * triggering a load.
 *
 * Backend returns a Laravel paginator wrapped in the standard
 * `{success, data: { current_page, data: [rows], total, per_page, ... }}`
 * envelope (see WEB-3328). Each row carries `from_date`, `to_date`,
 * `property_ids` (CSV string — backend supports multi-property reports),
 * `created_at`, `created_by`, `status`, `version`, `url` (presigned S3),
 * `s3_file_url`, and `record_count`. Regenerate eligibility currently has
 * to be inferred from `status !== 'superseded'` because the backend does
 * not yet emit an `is_regeneratable` flag on the listing.
 */
class SageReportHistoryController {

    constructor($scope, SSHttpService, SSAlertService, SSUserService, SSUtilService, SSConfirmationService, SageReportsService, $timeout) {
        const self = this;

        self.$scope = $scope;
        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSUserService = SSUserService;
        self.SSUtilService = SSUtilService;
        self.SSConfirmationService = SSConfirmationService;
        self.SageReportsService = SageReportsService;
        self.$timeout = $timeout;

        self.REPORT_TYPE_INVOICE = 'invoice';
        self.REPORT_TYPE_RECEIPT = 'receipt';

        self.STATUSES = ['generated', 'regenerated', 'superseded'];

        // Permissions (post-WEB-3328 iter-16 rename). All global gates; project
        // scoping comes from DMS_VW_PROJ, which the property-list endpoint
        // already filters by. Per-row buttons also re-check DMS_VW_PROJ on
        // the row's property as a defensive belt-and-braces.
        self.PERMISSION_VIEW = 'DMS_SAGE_EXPORT_HISTORY_VIEW';
        self.PERMISSION_DOWNLOAD = 'DMS_SAGE_EXPORT_HISTORY_DOWNLOAD';
        self.PERMISSION_REGENERATE = 'DMS_SAGE_EXPORT_HISTORY_REGENERATE';

        self.has_access = self.SSUserService.hasPermission(self.PERMISSION_VIEW);

        self.properties = null;
        self.selectedPropertyId = null;
        self.filters = self._defaultFilters();
        self.rows = [];
        self.total = 0;
        self.page = 1;
        self.perPage = 20;
        self.lastPage = 1;
        self.loading = false;
        self.searched = false;
        self.datepickerOptions = { showWeeks: false };
        self.displayDateFrom = false;
        self.displayDateTo = false;

        if (self.has_access) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.prepareDeveloperProperties();
        self.initDatepickers();
    }

    /**
     * Wire the From / To Date inputs to jQuery UI datepicker — same widget
     * used by the Sales & Payments / SPA Dates pages for visual consistency.
     * `onSelect` pushes the picked value back into ng-model via $scope.$apply
     * because jQuery UI fires its select callback outside Angular's digest.
     * Wrapped in $timeout(0) so the DOM nodes are in place before init.
     */
    initDatepickers() {
        const self = this;
        self.$timeout(function () {
            $('#sageHistoryFromDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () { self.filters.from = dateText; });
                }
            });
            $('#sageHistoryToDate').datepicker({
                changeMonth: true,
                changeYear: true,
                dateFormat: 'dd/mm/yy',
                onSelect: function (dateText) {
                    self.$scope.$apply(function () { self.filters.to = dateText; });
                }
            });
        });
    }

    _defaultFilters() {
        return {
            report_type: this.REPORT_TYPE_INVOICE,
            status: '',
            from: null,
            to: null
        };
    }

    /**
     * Fetch the user's permitted projects. The backend list is already scoped
     * to projects the user has `DMS_VW_PROJ` on; the `status` filter narrows
     * it further to projects currently in the LISTED state — SAGE exports
     * only apply to live projects, so the history filter mirrors that scope.
     */
    prepareDeveloperProperties() {
        const self = this;

        let params = { status: SHOWSUITE.PROPERTY_STATUS_LISTED };

        loading(true);
        self.SSHttpService.getAPIRequest('sec/developer/properties/list', params).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            let all = (response.data && response.data.properties) || [];
            // Defensive: also filter client-side. The backend may not honour
            // the `status` query param on sec/developer/properties/list, so
            // this guarantees the dropdown shows only LISTED projects.
            self.properties = all.filter(function (p) {
                return p && p.status === SHOWSUITE.PROPERTY_STATUS_LISTED;
            });

            // Cross-page project memory — share the `lastPropertyId` key
            // with Sales & Payments and the SAGE generate pages so the
            // same project stays selected as the user moves between them.
            // Only pre-select if the saved project is in this page's
            // (LISTED-filtered) list; otherwise leave the dropdown empty
            // and do NOT overwrite the saved value.
            let saved = self.SSUtilService.getFromLocalStorage('lastPropertyId');
            if (saved && self.properties.some(p => String(p.id) === String(saved))) {
                self.selectedPropertyId = parseInt(saved, 10);
            }

            self.$timeout(function () {
                if ($('select.dashboard-select#property-select').niceSelect) {
                    $('select.dashboard-select#property-select').niceSelect('update');
                }
            });
        });
    }

    // -- View handlers -- (do NOT auto-fetch; user clicks Search) ------------

    onPropertyChange() {
        const self = this;
        self._resetResults();

        // Persist selected project so other pages (Sales & Payments,
        // SAGE generate pages) pick it up on next visit.
        if (self.selectedPropertyId) {
            self.SSUtilService.saveToLocalStorage('lastPropertyId', self.selectedPropertyId);
        }
    }

    onReportTypeChange(type) {
        const self = this;
        if (self.filters.report_type === type) { return; }
        self.filters.report_type = type;
        self._resetResults();
    }

    openDateFrom() { this.displayDateFrom = true; }
    openDateTo() { this.displayDateTo = true; }

    _resetResults() {
        const self = this;
        self.rows = [];
        self.total = 0;
        self.page = 1;
        self.searched = false;
    }

    /**
     * User-triggered search — fetch page 1 with current filters. Validates
     * that a project is selected first.
     */
    search() {
        const self = this;
        if (!self.selectedPropertyId) {
            self.SSAlertService.danger('Validation Error', 'Please select a project first.');
            return;
        }
        self.page = 1;
        self.fetch();
    }

    fetch() {
        const self = this;

        if (!self.selectedPropertyId) { return; }

        let params = {
            property_id: self.selectedPropertyId,
            per_page: self.perPage,
            page: self.page
        };

        if (self.filters.status) { params.status = self.filters.status; }
        if (self.filters.from) { params.from = self._fmtDate(self.filters.from); }
        if (self.filters.to) { params.to = self._fmtDate(self.filters.to); }

        let request = (self.filters.report_type === self.REPORT_TYPE_RECEIPT)
            ? self.SageReportsService.getReceiptHistory(params)
            : self.SageReportsService.getInvoiceHistory(params);

        self.loading = true;
        loading(true);

        request.then(function (response) {
            loading(false);
            self.loading = false;
            self.searched = true;

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            // Two shapes supported:
            //
            // Iter-17 (current/future) — wrapped envelope:
            //   { data: { invoice_history: [...], pagination: { ... } } }
            //   { data: { receipt_history: [...], pagination: { ... } } }
            //
            // Pre-iter-17 (legacy) — raw Laravel paginator:
            //   { data: [...rows], current_page, total, per_page, last_page }
            //
            // The iter-17 shape is rolling out via ShowsuiteAPI #1110/#1111;
            // until those deploy the listing still returns the old shape.
            let page = response.data || {};
            let rowsKey = self.filters.report_type === self.REPORT_TYPE_RECEIPT
                ? 'receipt_history'
                : 'invoice_history';
            let pagination = page.pagination || {};

            self.rows     = page[rowsKey] || page.data || [];
            self.total    = pagination.total        || page.total        || self.rows.length;
            self.page     = pagination.current_page || page.current_page || self.page;
            self.perPage  = pagination.per_page     || page.per_page     || self.perPage;
            self.lastPage = pagination.last_page    || page.last_page    || 1;
        });
    }

    // -- Per-row helpers -----------------------------------------------------

    /**
     * Human label for a row's report_type, shown in the listing's Type column.
     *
     * Receipt exports are now split into principal vs GST (WEB-3536) and the
     * backend returns both under one receipt history response, tagged with
     * report_type `sage_receipt_principal` / `sage_receipt_gst`. Map those to
     * "Principal" / "GST" so the two are distinguishable in the shared table.
     * Legacy single-type receipt rows (`sage_receipt`) and invoice rows fall
     * back to friendly labels; anything unrecognised shows the raw value.
     */
    reportTypeLabel(row) {
        if (!row || !row.report_type) { return ''; }
        switch (row.report_type) {
            case 'sage_receipt_principal': return 'Principal';
            case 'sage_receipt_gst':       return 'GST';
            case 'sage_receipt':           return 'Receipt';
            case 'sage_invoice':           return 'Invoice';
            default:                       return row.report_type;
        }
    }

    /**
     * Join property names for a row's property_ids CSV by looking them up in
     * the loaded properties list. Falls back to the raw IDs when names are
     * unavailable.
     */
    propertyNamesFor(row) {
        const self = this;
        if (!row || !row.property_ids) { return ''; }
        let ids = String(row.property_ids).split(',').map(s => s.trim()).filter(Boolean);
        if (!self.properties || !self.properties.length) { return ids.join(', '); }
        return ids.map(function (id) {
            let prop = self.properties.find(function (p) { return String(p.id) === id; });
            return prop ? prop.name : id;
        }).join(', ');
    }

    /**
     * Whether to show the Download action for this row. Requires the
     * download permission globally and DMS_VW_PROJ on the row's project
     * (the latter is implicit since the project picker is already filtered
     * by it, but we re-check defensively).
     */
    canDownload(row) {
        const self = this;
        if (!row) { return false; }
        if (!self.SSUserService.hasPermission(self.PERMISSION_DOWNLOAD)) { return false; }
        return self._hasVwProjOn(row);
    }

    /**
     * Whether to show the Regenerate action for this row.
     *
     * Permission gates:
     *   - DMS_SAGE_EXPORT_HISTORY_REGENERATE (global)
     *   - DMS_VW_PROJ on the row's property
     *
     * Eligibility — the matrix specifies `row.is_regeneratable === true`,
     * but the listing endpoint doesn't always emit the flag yet, and Laravel
     * sometimes serialises booleans as 0/1 or '0'/'1' strings. Accept all
     * those forms, and when the field is missing entirely fall back to a
     * status check (non-superseded rows are eligible). The regenerate POST
     * itself returns 409 when the row truly can't be regenerated, which the
     * controller already surfaces gracefully — that's the authoritative gate.
     */
    canRegenerate(row) {
        const self = this;
        if (!row) { return false; }
        if (!self.SSUserService.hasPermission(self.PERMISSION_REGENERATE)) { return false; }
        if (!self._hasVwProjOn(row)) { return false; }

        let flag = row.is_regeneratable;
        if (flag === true || flag === 1 || flag === '1') { return true; }
        if (flag === false || flag === 0 || flag === '0') { return false; }
        return row.status !== 'superseded';
    }

    /**
     * Check DMS_VW_PROJ on the row's first property. Returns true if the
     * row carries no property id (defensive — backend should always set
     * one, but we don't want a missing field to silently hide an action).
     */
    _hasVwProjOn(row) {
        const self = this;
        let pid = self.firstPropertyId(row);
        if (!pid) { return true; }
        return self.SSUserService.hasPermissionForProperty('DMS_VW_PROJ', pid);
    }

    /**
     * Trigger CSV download for a history row. The S3 URLs embedded in the
     * listing rows (`url` / `s3_file_url`) are not directly fetchable —
     * they need to be re-signed per request. Call the dedicated download
     * endpoint instead and open the freshly-signed URL it returns.
     */
    download(row) {
        const self = this;
        if (!row || !row.id) { return; }

        let propertyId = self.firstPropertyId(row);
        // Receipt rows now carry split report types (sage_receipt_principal /
        // sage_receipt_gst) as well as the legacy `sage_receipt` — match the
        // shared `sage_receipt` prefix. The active-tab fallback still covers
        // rows that arrive without a report_type set.
        let isReceipt = (String(row.report_type || '').indexOf('sage_receipt') === 0)
            || (self.filters.report_type === self.REPORT_TYPE_RECEIPT);

        let request = isReceipt
            ? self.SageReportsService.getReceiptDownloadUrl(row.id, propertyId)
            : self.SageReportsService.getInvoiceDownloadUrl(row.id, propertyId);

        loading(true);
        request.then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            // The download endpoint is exempt from the iter-17 wrap and
            // returns `{ download_url, history }` directly at the root.
            // Check `download_url` first, then fall through other field
            // names defensively — older sandbox builds and the embedded
            // listing rows have used `url` / `presigned_url` / `s3_file_url`.
            let payload = (response && response.data) || {};
            let url = payload.download_url
                || payload.presigned_url
                || payload.url
                || payload.s3_file_url
                || response.download_url
                || response.presigned_url
                || response.url;

            if (url) {
                window.open(url, '_blank');
            } else {
                self.SSAlertService.danger(
                    'Download Failed',
                    'The server did not return a downloadable URL for this row.'
                );
            }
        });
    }

    /**
     * Resolve a row's first property id (rows can carry a CSV of property_ids
     * when allow_multiple_properties is set). Used for the download endpoint
     * which is project-scoped.
     */
    firstPropertyId(row) {
        if (!row) { return null; }
        if (row.property_id) { return row.property_id; }
        let ids = String(row.property_ids || '').split(',').map(s => s.trim()).filter(Boolean);
        return ids.length ? ids[0] : (this.selectedPropertyId || null);
    }

    /**
     * Trigger regeneration for the given history row. On success the backend
     * returns the freshly-generated CSV body (text/csv + Content-Disposition);
     * we save it via the same blob-download path used by the generate
     * endpoints, then refresh the listing so the new version + flipped prior
     * status appear.
     *
     * Backend currently exposes regenerate as a wrapped JSON endpoint (not
     * a CSV stream) — when that's the case the call falls through the
     * existing SSHttpService flow and we open the returned URL.
     */
    regenerate(row) {
        const self = this;

        if (!self.canRegenerate(row)) { return; }

        self.SSConfirmationService.getConfirmed(
            'Regenerate report?',
            'A new version will be created and the prior version will be marked superseded.',
            self.SSConfirmationService.TYPE_DEFAULT,
            function () { self._doRegenerate(row); }
        );
    }

    /**
     * Hit the regenerate endpoint. The server streams the freshly-built
     * CSV body directly on success (text/csv + Content-Disposition:
     * attachment) and emits JSON only on failure — the service hands us
     * back `{ blob, filename }` on success or an Error-with-code on
     * failure, same shape as the generate flow.
     */
    _doRegenerate(row) {
        const self = this;

        loading(true);
        self.SageReportsService.regenerate(row.id).then(function (result) {
            loading(false);

            if (result instanceof Error) {
                if (result.code === 409) {
                    self.SSAlertService.warning(
                        'Cannot regenerate',
                        result.message || 'Nothing to regenerate — the row may already be superseded or no records are unlocked in this range.'
                    );
                    self.fetch(); // Refresh — state likely changed on the server.
                    return;
                }
                self.SSAlertService.parseAndDisplayError(result);
                return;
            }

            // Success: result = { blob, filename }. Trigger the browser save,
            // toast the user, and refresh the list — the new version row
            // appears and the prior row flips to `superseded`.
            self.SageReportsService.triggerCsvDownload(result.blob, result.filename);
            self.SSAlertService.success(
                'Report regenerated',
                'A new version has been created. Your browser has been prompted to save ' + (result.filename || 'the CSV file') + '.'
            );
            self.fetch();
        });
    }

    // -- Pagination ----------------------------------------------------------

    nextPage() {
        const self = this;
        if (self.page * self.perPage >= self.total) { return; }
        self.page += 1;
        self.fetch();
    }

    prevPage() {
        const self = this;
        if (self.page <= 1) { return; }
        self.page -= 1;
        self.fetch();
    }

    /**
     * Backend wants `yyyy-mm-dd`. jQuery UI datepicker pushes a `dd/mm/yyyy`
     * string into ng-model on select (or whatever the user typed); the
     * legacy uib-datepicker pushed a Date object. Handle all three.
     */
    _fmtDate(d) {
        if (!d) { return null; }
        if (typeof d === 'string') {
            // jQuery UI / typed format: dd/mm/yyyy → yyyy-mm-dd
            let parts = d.split('/');
            if (parts.length === 3 && parts[2].length === 4) {
                return parts[2] + '-' + parts[1] + '-' + parts[0];
            }
            // Already ISO (or unknown shape) — pass through.
            return d;
        }
        let pad = (n) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    }
}

SageReportHistoryController.$inject = [
    '$scope',
    'SSHttpService',
    'SSAlertService',
    'SSUserService',
    'SSUtilService',
    'SSConfirmationService',
    'SageReportsService',
    '$timeout'
];
app.controller('SageReportHistoryController', SageReportHistoryController);
